// Wise Check Question Structure
export const CONSTRAINT_BUCKETS = {
  DEMAND: 'Demand / Marketing',
  CONVERSION: 'Conversion / Sales',
  DELIVERY: 'Delivery / Operations',
  FINANCIAL: 'Financial Clarity',
  PEOPLE: 'People / Capacity'
};

export const QUESTIONS = {
  // NODE 1 - CONTEXT SETTER
  Q1: {
    id: 'Q1',
    node: 'NODE_1',
    question: 'Which best describes your business today?',
    type: 'single',
    options: [
      { value: 'growing_stretched', label: 'Growing but stretched', next: 'Q2' },
      { value: 'flat_inconsistent', label: 'Flat or inconsistent', next: 'Q2' },
      { value: 'stable_modernise', label: 'Stable but want to modernise', next: 'Q2' },
      { value: 'under_pressure', label: 'Under pressure / reactive', next: 'Q2' }
    ]
  },

  // NODE 2 - PRIMARY CONSTRAINT
  Q2: {
    id: 'Q2',
    node: 'NODE_2',
    question: 'Which of these is creating the most friction right now?',
    type: 'single',
    options: [
      { value: 'not_enough_leads', label: 'Not enough leads / enquiries', next: 'Q3M', path: 'MARKETING', score: { [CONSTRAINT_BUCKETS.DEMAND]: 30 } },
      { value: 'leads_dont_convert', label: 'Leads come in but don\'t convert', next: 'Q3S', path: 'SALES', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 30 } },
      { value: 'delivery_inefficient', label: 'Delivery is inefficient', next: 'Q3O', path: 'OPERATIONS', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 30 } },
      { value: 'cash_margin_pressure', label: 'Cash flow / margin pressure', next: 'Q3F', path: 'FINANCIAL', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 30 } },
      { value: 'team_capacity', label: 'Team or capacity issues', next: 'Q3P', path: 'PEOPLE', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 30 } },
      { value: 'everything_connected', label: 'Unsure / everything connected', next: 'Q3D', path: 'DIAGNOSTIC', score: { [CONSTRAINT_BUCKETS.DEMAND]: 15, [CONSTRAINT_BUCKETS.DELIVERY]: 15 } }
    ]
  },

  // MARKETING PATH
  Q3M: {
    id: 'Q3M',
    question: 'What is your main source of leads?',
    type: 'single',
    options: [
      { value: 'referrals', label: 'Referrals', next: 'Q4M' },
      { value: 'paid', label: 'Paid advertising', next: 'Q4M' },
      { value: 'social', label: 'Social media', next: 'Q4M' },
      { value: 'unsure', label: 'Unsure / mixed', next: 'Q4M', score: { [CONSTRAINT_BUCKETS.DEMAND]: 5 } }
    ]
  },

  Q4M: {
    id: 'Q4M',
    question: 'Are your leads predictable month-to-month?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes', next: 'Q5M' },
      { value: 'no', label: 'No', next: 'Q5M', score: { [CONSTRAINT_BUCKETS.DEMAND]: 10 } }
    ]
  },

  Q5M: {
    id: 'Q5M',
    question: 'Do you know your cost per lead?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes', next: 'Q6M' },
      { value: 'no', label: 'No', next: 'Q6M', score: { [CONSTRAINT_BUCKETS.DEMAND]: 10 }, flag: 'no_tracking' }
    ]
  },

  Q6M: {
    id: 'Q6M',
    question: 'How do you follow up on leads?',
    type: 'single',
    options: [
      { value: 'immediate', label: 'Immediate', next: 'Q7M' },
      { value: 'delayed', label: 'Delayed', next: 'Q7M', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 10 } },
      { value: 'inconsistent', label: 'Inconsistent', next: 'Q7M', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 15 } }
    ]
  },

  Q7M: {
    id: 'Q7M',
    question: 'What is the main issue with your leads?',
    type: 'single',
    options: [
      { value: 'volume', label: 'Not enough volume', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.DEMAND]: 15 } },
      { value: 'quality', label: 'Poor quality', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.DEMAND]: 15 } },
      { value: 'messaging', label: 'Unclear messaging', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.DEMAND]: 10 } },
      { value: 'unsure', label: 'Unsure', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.DEMAND]: 5 } }
    ]
  },

  // SALES / PROCESS PATH
  Q3S: {
    id: 'Q3S',
    question: 'Do you have a defined sales process?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes', next: 'Q4S' },
      { value: 'informal', label: 'Informal', next: 'Q4S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 10 } },
      { value: 'no', label: 'No', next: 'Q4S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 15 }, flag: 'no_process' }
    ]
  },

  Q4S: {
    id: 'Q4S',
    question: 'How are leads tracked?',
    type: 'single',
    options: [
      { value: 'crm', label: 'CRM system', next: 'Q5S' },
      { value: 'spreadsheet', label: 'Spreadsheet', next: 'Q5S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 5 } },
      { value: 'email', label: 'Email only', next: 'Q5S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 10 } },
      { value: 'none', label: 'Not tracked', next: 'Q5S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 15 }, flag: 'no_tracking' }
    ]
  },

  Q5S: {
    id: 'Q5S',
    question: 'Where do deals typically fall over?',
    type: 'single',
    options: [
      { value: 'no_response', label: 'No response from leads', next: 'Q6S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 10 } },
      { value: 'price', label: 'Price objections', next: 'Q6S', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 10 } },
      { value: 'slow_followup', label: 'Slow follow-up', next: 'Q6S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 15 } },
      { value: 'internal_delays', label: 'Internal delays', next: 'Q6S', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 10 } }
    ]
  },

  Q6S: {
    id: 'Q6S',
    question: 'Is follow-up automated or manual?',
    type: 'single',
    options: [
      { value: 'automated', label: 'Automated', next: 'Q2B' },
      { value: 'manual', label: 'Manual', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 10 } },
      { value: 'mixed', label: 'Mixed', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 5 } }
    ]
  },

  // OPERATIONS PATH
  Q3O: {
    id: 'Q3O',
    question: 'How would you describe daily operations?',
    type: 'single',
    options: [
      { value: 'smooth', label: 'Smooth', next: 'Q4O' },
      { value: 'busy', label: 'Busy but manageable', next: 'Q4O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 5 } },
      { value: 'bottlenecks', label: 'Regular bottlenecks', next: 'Q4O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 15 } },
      { value: 'firefighting', label: 'Constant firefighting', next: 'Q4O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 15, [CONSTRAINT_BUCKETS.PEOPLE]: 10 } }
    ]
  },

  Q4O: {
    id: 'Q4O',
    question: 'Are your processes documented?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, comprehensively', next: 'Q5O' },
      { value: 'partial', label: 'Partially', next: 'Q5O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 10 } },
      { value: 'no', label: 'No', next: 'Q5O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 15 }, flag: 'no_documentation' }
    ]
  },

  Q5O: {
    id: 'Q5O',
    question: 'Where does work typically get stuck?',
    type: 'single',
    options: [
      { value: 'handover', label: 'Handover between people/teams', next: 'Q6O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 10, [CONSTRAINT_BUCKETS.PEOPLE]: 5 } },
      { value: 'approvals', label: 'Waiting for approvals', next: 'Q6O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 10 } },
      { value: 'responsibility', label: 'Unclear responsibility', next: 'Q6O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 15, [CONSTRAINT_BUCKETS.PEOPLE]: 10 } },
      { value: 'tools', label: 'Poor tools/systems', next: 'Q6O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 15 } }
    ]
  },

  Q6O: {
    id: 'Q6O',
    question: 'Is the business heavily reliant on key individuals?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 }, flag: 'key_person_risk' },
      { value: 'no', label: 'No', next: 'Q2B' }
    ]
  },

  // FINANCIAL PATH
  Q3F: {
    id: 'Q3F',
    question: 'What is your main financial issue?',
    type: 'single',
    options: [
      { value: 'cash_flow', label: 'Cash flow', next: 'Q4F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 15 } },
      { value: 'margin', label: 'Profit margins', next: 'Q4F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 15 } },
      { value: 'pricing', label: 'Pricing strategy', next: 'Q4F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 10 } },
      { value: 'forecasting', label: 'Forecasting/visibility', next: 'Q4F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 10 } }
    ]
  },

  Q4F: {
    id: 'Q4F',
    question: 'How often do you review financials?',
    type: 'single',
    options: [
      { value: 'monthly', label: 'Monthly', next: 'Q5F' },
      { value: 'quarterly', label: 'Quarterly', next: 'Q5F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 5 } },
      { value: 'rarely', label: 'Rarely', next: 'Q5F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 15 }, flag: 'no_visibility' }
    ]
  },

  Q5F: {
    id: 'Q5F',
    question: 'Can you answer "Which products/services make money?"',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes', next: 'Q2B' },
      { value: 'somewhat', label: 'Somewhat', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 10 } },
      { value: 'no', label: 'No', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 15 } }
    ]
  },

  // PEOPLE PATH
  Q3P: {
    id: 'Q3P',
    question: 'What is your main people challenge?',
    type: 'single',
    options: [
      { value: 'capacity', label: 'Capacity / too much work', next: 'Q4P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 } },
      { value: 'accountability', label: 'Accountability', next: 'Q4P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 } },
      { value: 'skill_gaps', label: 'Skill gaps', next: 'Q4P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 10 } },
      { value: 'motivation', label: 'Motivation / engagement', next: 'Q4P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 10 } }
    ]
  },

  Q4P: {
    id: 'Q4P',
    question: 'Are roles and expectations clear?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes', next: 'Q5P' },
      { value: 'somewhat', label: 'Somewhat', next: 'Q5P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 10 } },
      { value: 'no', label: 'No', next: 'Q5P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 } }
    ]
  },

  Q5P: {
    id: 'Q5P',
    question: 'How dependent is the business on you personally?',
    type: 'single',
    options: [
      { value: 'high', label: 'High', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 }, flag: 'owner_dependent' },
      { value: 'medium', label: 'Medium', next: 'Q2B', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 10 } },
      { value: 'low', label: 'Low', next: 'Q2B' }
    ]
  },

  // DIAGNOSTIC PATH
  Q3D: {
    id: 'Q3D',
    question: 'Which area causes you the most stress personally?',
    type: 'single',
    options: [
      { value: 'money', label: 'Money / financial pressure', next: 'Q3F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 15 } },
      { value: 'time', label: 'Time / workload', next: 'Q3O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 10, [CONSTRAINT_BUCKETS.PEOPLE]: 10 } },
      { value: 'team', label: 'Team issues', next: 'Q3P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 } },
      { value: 'growth', label: 'Growth uncertainty', next: 'Q3M', score: { [CONSTRAINT_BUCKETS.DEMAND]: 15 } }
    ]
  },

  // NODE 2B - SECONDARY CONSTRAINT LOOP
  Q2B: {
    id: 'Q2B',
    node: 'NODE_2B',
    question: 'Aside from that, are you also facing any of the following?',
    type: 'single',  // Changed from 'multiple' to 'single'
    optional: true,
    options: [
      { value: 'lead_generation', label: 'Lead generation issues', next: 'Q3M', score: { [CONSTRAINT_BUCKETS.DEMAND]: 15 } },
      { value: 'conversion', label: 'Conversion / follow-up issues', next: 'Q3S', score: { [CONSTRAINT_BUCKETS.CONVERSION]: 15 } },
      { value: 'operational', label: 'Operational inefficiencies', next: 'Q3O', score: { [CONSTRAINT_BUCKETS.DELIVERY]: 15 } },
      { value: 'financial', label: 'Financial pressure', next: 'Q3F', score: { [CONSTRAINT_BUCKETS.FINANCIAL]: 15 } },
      { value: 'team_gaps', label: 'Team / capability gaps', next: 'Q3P', score: { [CONSTRAINT_BUCKETS.PEOPLE]: 15 } },
      { value: 'none', label: 'No — that\'s the main issue', next: 'INDUSTRY' }
    ]
  },

  // NODE 3 - CONTEXT MODIFIERS
  INDUSTRY: {
    id: 'INDUSTRY',
    node: 'NODE_3',
    question: 'What industry are you in?',
    type: 'text',
    next: 'TEAM_SIZE'
  },

  TEAM_SIZE: {
    id: 'TEAM_SIZE',
    question: 'What is your team size?',
    type: 'single',
    options: [
      { value: 'solo', label: 'Solo', next: 'YEARS_OPERATING' },
      { value: '2-5', label: '2–5 people', next: 'YEARS_OPERATING' },
      { value: '6-15', label: '6–15 people', next: 'YEARS_OPERATING' },
      { value: '16+', label: '16+ people', next: 'YEARS_OPERATING' }
    ]
  },

  YEARS_OPERATING: {
    id: 'YEARS_OPERATING',
    question: 'How long have you been operating?',
    type: 'single',
    options: [
      { value: 'less_2', label: 'Less than 2 years', next: 'Q4' },
      { value: '2-5', label: '2–5 years', next: 'Q4' },
      { value: '5+', label: '5+ years', next: 'Q4' }
    ]
  },

  // NODE 4 - READINESS
  Q4: {
    id: 'Q4',
    node: 'NODE_4',
    question: 'Which best describes your current focus?',
    type: 'single',
    options: [
      { value: 'fix_broken', label: 'Fix broken systems', next: 'Q5' },
      { value: 'improve', label: 'Improve efficiency', next: 'Q5' },
      { value: 'prepare', label: 'Prepare for growth', next: 'Q5' },
      { value: 'scale', label: 'Scale faster', next: 'Q5' }
    ]
  },

  // NODE 5 - IMPACT
  Q5: {
    id: 'Q5',
    node: 'NODE_5',
    question: 'If your main issue was solved in 90 days, what would change most?',
    type: 'single',
    options: [
      { value: 'revenue', label: 'Revenue', next: 'Q6' },
      { value: 'time', label: 'Time / headspace', next: 'Q6' },
      { value: 'stress', label: 'Stress levels', next: 'Q6' },
      { value: 'confidence', label: 'Confidence / clarity', next: 'Q6' }
    ]
  },

  // NODE 6 - PERMISSION
  Q6: {
    id: 'Q6',
    node: 'NODE_6',
    question: 'Would you like a clear recommendation on next steps?',
    type: 'single',
    options: [
      { value: 'tailored', label: 'Yes — tailored actions', next: 'CONTACT' },
      { value: 'roadmap', label: 'Yes — roadmap', next: 'CONTACT' },
      { value: 'diagnosis', label: 'Just diagnosis', next: 'RESULTS' }
    ]
  },

  // CONTACT INFO
  CONTACT: {
    id: 'CONTACT',
    question: 'Please provide your contact details',
    type: 'contact',
    fields: ['name', 'email', 'phone', 'business_name'],
    next: 'RESULTS'
  }
};